import type { GeoPoint } from "./amapChargingDemo";

export const WAITING_ROUTE_MIN_MINUTES = 10;
export const WAITING_ROUTE_MAX_MINUTES = 16;
export const WAITING_ROUTE_DEFAULT_BAND_ID: WaitingRouteBandId = "10-13";

export const LONGYAO_WAITING_DESTINATION = {
  id: "BX10016305",
  name: "龙耀路地铁站2号口",
  location: {
    lng: 121.460033,
    lat: 31.159235
  }
} as const;

export const LONGYAO_WAITING_DEMO_ORIGIN = {
  id: LONGYAO_WAITING_DESTINATION.id,
  name: LONGYAO_WAITING_DESTINATION.name,
  location: LONGYAO_WAITING_DESTINATION.location
} as const;

export interface WaitingRouteCandidate {
  id: string;
  label: string;
  expectedDurationSeconds: number;
  snapshotDistanceKm: number;
  snapshotTrafficLights: number;
  snapshotManeuvers: WaitingRouteSnapshotManeuver[];
  snapshotPath: GeoPoint[];
  waypoints: GeoPoint[];
}

export interface WaitingRouteSnapshotManeuver {
  action: string;
  assistantAction: string;
  instruction: string;
}

export type WaitingRouteBandId = "10-13" | "13-16";

export interface WaitingRouteBand {
  id: WaitingRouteBandId;
  label: string;
  title: string;
  minMinutes: number;
  maxMinutes: number;
  route: WaitingRouteCandidate;
}

export interface WaitingRoutePoi {
  id: string;
  name: string;
  category: "地铁" | "商业" | "文化" | "餐饮" | "会展";
  location: GeoPoint;
}

export interface WaitingRouteDemoState {
  bandId: WaitingRouteBandId;
  revision: number;
}

export interface WaitingRouteIntent {
  requestedMinutes: number;
  bandId: WaitingRouteBandId;
  wasClamped: boolean;
}

const CLOCKWISE_WEST_ANCHOR: GeoPoint = { lng: 121.4594, lat: 31.1598 };
const CLOCKWISE_MEDIUM_EAST_ANCHOR: GeoPoint = { lng: 121.462, lat: 31.1598 };
const CLOCKWISE_LONG_EAST_ANCHOR: GeoPoint = { lng: 121.4645, lat: 31.1598 };

// These are versioned, AMap-snapped road polylines captured from the same
// right-turn-only requests used at runtime. They keep the demo on real roads
// while AMap is loading or temporarily unavailable; they are never generated
// by drawing straight lines between waypoints.
const CLOCKWISE_SHORT_ROAD_SNAPSHOT: GeoPoint[] = [
  { lng: 121.460148, lat: 31.159247 },
  { lng: 121.460106, lat: 31.159533 },
  { lng: 121.460074, lat: 31.159763 },
  { lng: 121.461308, lat: 31.159919 },
  { lng: 121.462671, lat: 31.160085 },
  { lng: 121.463089, lat: 31.160133 },
  { lng: 121.463346, lat: 31.160182 },
  { lng: 121.463647, lat: 31.160235 },
  { lng: 121.463813, lat: 31.160257 },
  { lng: 121.4648, lat: 31.160391 },
  { lng: 121.464875, lat: 31.160402 },
  { lng: 121.464854, lat: 31.159994 },
  { lng: 121.464843, lat: 31.159929 },
  { lng: 121.464811, lat: 31.159763 },
  { lng: 121.464807, lat: 31.159748 },
  { lng: 121.464741, lat: 31.159506 },
  { lng: 121.464671, lat: 31.159307 },
  { lng: 121.464575, lat: 31.159103 },
  { lng: 121.464489, lat: 31.159098 },
  { lng: 121.46399, lat: 31.159082 },
  { lng: 121.463931, lat: 31.159077 },
  { lng: 121.463808, lat: 31.159061 },
  { lng: 121.462821, lat: 31.158916 },
  { lng: 121.461501, lat: 31.158776 },
  { lng: 121.46024, lat: 31.15861 },
  { lng: 121.460203, lat: 31.158605 },
  { lng: 121.460037, lat: 31.158594 },
  { lng: 121.459956, lat: 31.158588 },
  { lng: 121.459771, lat: 31.159708 },
  { lng: 121.459768, lat: 31.159726 },
  { lng: 121.459843, lat: 31.159731 },
  { lng: 121.460074, lat: 31.159763 },
  { lng: 121.460106, lat: 31.159533 },
  { lng: 121.460148, lat: 31.159247 }
];

const CLOCKWISE_LONG_ROAD_SNAPSHOT: GeoPoint[] = [
  { lng: 121.460148, lat: 31.159247 },
  { lng: 121.460106, lat: 31.159533 },
  { lng: 121.460074, lat: 31.159763 },
  { lng: 121.461308, lat: 31.159919 },
  { lng: 121.461966, lat: 31.159999 },
  { lng: 121.462671, lat: 31.160085 },
  { lng: 121.462719, lat: 31.15972 },
  { lng: 121.462746, lat: 31.159511 },
  { lng: 121.462762, lat: 31.159393 },
  { lng: 121.462778, lat: 31.15927 },
  { lng: 121.462789, lat: 31.159157 },
  { lng: 121.462821, lat: 31.158916 },
  { lng: 121.461501, lat: 31.158776 },
  { lng: 121.46024, lat: 31.15861 },
  { lng: 121.460203, lat: 31.158605 },
  { lng: 121.460037, lat: 31.158594 },
  { lng: 121.459956, lat: 31.158588 },
  { lng: 121.459771, lat: 31.159708 },
  { lng: 121.459768, lat: 31.159726 },
  { lng: 121.459843, lat: 31.159731 },
  { lng: 121.460074, lat: 31.159763 },
  { lng: 121.461308, lat: 31.159919 },
  { lng: 121.462671, lat: 31.160085 },
  { lng: 121.463089, lat: 31.160133 },
  { lng: 121.463346, lat: 31.160182 },
  { lng: 121.463647, lat: 31.160235 },
  { lng: 121.463813, lat: 31.160257 },
  { lng: 121.4648, lat: 31.160391 },
  { lng: 121.464875, lat: 31.160402 },
  { lng: 121.464854, lat: 31.159994 },
  { lng: 121.464843, lat: 31.159929 },
  { lng: 121.464811, lat: 31.159763 },
  { lng: 121.464804, lat: 31.159739 },
  { lng: 121.464741, lat: 31.159506 },
  { lng: 121.464671, lat: 31.159307 },
  { lng: 121.464575, lat: 31.159103 },
  { lng: 121.464489, lat: 31.159098 },
  { lng: 121.46399, lat: 31.159082 },
  { lng: 121.463931, lat: 31.159077 },
  { lng: 121.463808, lat: 31.159061 },
  { lng: 121.462821, lat: 31.158916 },
  { lng: 121.461501, lat: 31.158776 },
  { lng: 121.46024, lat: 31.15861 },
  { lng: 121.460203, lat: 31.158605 },
  { lng: 121.460037, lat: 31.158594 },
  { lng: 121.459956, lat: 31.158588 },
  { lng: 121.459771, lat: 31.159708 },
  { lng: 121.459768, lat: 31.159726 },
  { lng: 121.459843, lat: 31.159731 },
  { lng: 121.460074, lat: 31.159763 },
  { lng: 121.460106, lat: 31.159533 },
  { lng: 121.460148, lat: 31.159247 }
];

const CLOCKWISE_SHORT_ROAD_MANEUVERS: WaitingRouteSnapshotManeuver[] = [
  { action: "右转", assistantAction: "", instruction: "向北行驶57米右转" },
  { action: "右转", assistantAction: "进入主路", instruction: "沿龙文路向东行驶453米右转进入主路" },
  { action: "", assistantAction: "到达途经地", instruction: "沿龙腾大道向南行驶74米到达途经地" },
  { action: "右转", assistantAction: "", instruction: "沿龙腾大道向南行驶74米右转" },
  { action: "右转", assistantAction: "进入主路", instruction: "沿龙台路向西行驶424米右转进入主路" },
  { action: "", assistantAction: "到达途经地", instruction: "沿云锦路向北行驶126米到达途经地" },
  { action: "右转", assistantAction: "", instruction: "沿云锦路向北行驶2米右转" },
  { action: "右转", assistantAction: "", instruction: "沿龙文路向东行驶22米右转" },
  { action: "", assistantAction: "到达目的地", instruction: "向南行驶57米到达目的地" }
];

const CLOCKWISE_LONG_ROAD_MANEUVERS: WaitingRouteSnapshotManeuver[] = [
  { action: "右转", assistantAction: "", instruction: "向北行驶57米右转" },
  { action: "", assistantAction: "到达途经地", instruction: "沿龙文路向东行驶181米到达途经地" },
  { action: "右转", assistantAction: "", instruction: "沿龙文路向东行驶68米右转" },
  { action: "右转", assistantAction: "", instruction: "沿云绣路向南行驶128米右转" },
  { action: "右转", assistantAction: "进入主路", instruction: "沿龙台路向西行驶265米右转进入主路" },
  { action: "", assistantAction: "到达途经地", instruction: "沿云锦路向北行驶126米到达途经地" },
  { action: "右转", assistantAction: "", instruction: "沿云锦路向北行驶2米右转" },
  { action: "右转", assistantAction: "进入主路", instruction: "沿龙文路向东行驶475米右转进入主路" },
  { action: "", assistantAction: "到达途经地", instruction: "沿龙腾大道向南行驶74米到达途经地" },
  { action: "右转", assistantAction: "", instruction: "沿龙腾大道向南行驶74米右转" },
  { action: "右转", assistantAction: "进入主路", instruction: "沿龙台路向西行驶424米右转进入主路" },
  { action: "", assistantAction: "到达途经地", instruction: "沿云锦路向北行驶126米到达途经地" },
  { action: "右转", assistantAction: "", instruction: "沿云锦路向北行驶2米右转" },
  { action: "右转", assistantAction: "", instruction: "沿龙文路向东行驶22米右转" },
  { action: "", assistantAction: "到达目的地", instruction: "向南行驶57米到达目的地" }
];

function createClockwiseLoops(eastAnchors: GeoPoint[]) {
  return eastAnchors.flatMap((eastAnchor) => [
    { ...eastAnchor },
    { ...CLOCKWISE_WEST_ANCHOR }
  ]);
}

// Each pair of anchors forces one clockwise block loop. AMap has repeatedly
// returned right-turn-only steps for these sequences. Runtime maneuver checks
// remain authoritative because live traffic and routing algorithms can change.
export const WAITING_ROUTE_BANDS: WaitingRouteBand[] = [
  {
    id: "10-13",
    label: "10–13分钟",
    title: "短绕行",
    minMinutes: 10,
    maxMinutes: 13,
    route: {
      id: "clockwise-road-loop-v2",
      label: "龙耀路顺时针道路环线",
      expectedDurationSeconds: 613,
      snapshotDistanceKm: 1.289,
      snapshotTrafficLights: 6,
      snapshotManeuvers: CLOCKWISE_SHORT_ROAD_MANEUVERS,
      snapshotPath: CLOCKWISE_SHORT_ROAD_SNAPSHOT,
      waypoints: createClockwiseLoops([CLOCKWISE_LONG_EAST_ANCHOR])
    }
  },
  {
    id: "13-16",
    label: "13–16分钟",
    title: "长绕行",
    minMinutes: 13,
    maxMinutes: 16,
    route: {
      id: "clockwise-road-double-loop-v2",
      label: "龙耀路顺时针双道路环线",
      expectedDurationSeconds: 931,
      snapshotDistanceKm: 2.081,
      snapshotTrafficLights: 11,
      snapshotManeuvers: CLOCKWISE_LONG_ROAD_MANEUVERS,
      snapshotPath: CLOCKWISE_LONG_ROAD_SNAPSHOT,
      waypoints: createClockwiseLoops([
        CLOCKWISE_MEDIUM_EAST_ANCHOR,
        CLOCKWISE_LONG_EAST_ANCHOR
      ])
    }
  }
];

// Live AMap mode uses the native point layer. These verified landmarks keep the
// local fallback map believable without changing route bounds or API usage.
export const LONGYAO_WAITING_FALLBACK_POIS: WaitingRoutePoi[] = [
  {
    id: "B0FFKW05R7",
    name: "LUMINA星扬西岸中心",
    category: "商业",
    location: { lng: 121.461225, lat: 31.161591 }
  },
  {
    id: "B0H37CDU2T",
    name: "麦当劳中国总部大楼",
    category: "餐饮",
    location: { lng: 121.463729, lat: 31.158708 }
  },
  {
    id: "B0LRNMKBDV",
    name: "西岸国际会展中心",
    category: "会展",
    location: { lng: 121.463315, lat: 31.162193 }
  },
  {
    id: "B0H0OSXDUD",
    name: "GATE M西岸梦中心",
    category: "商业",
    location: { lng: 121.465465, lat: 31.16119 }
  },
  {
    id: "B0GKX5DOBT",
    name: "GATE M西岸凤巢",
    category: "商业",
    location: { lng: 121.460353, lat: 31.163444 }
  },
  {
    id: "B0FFHUD4BQ",
    name: "上海油罐艺术中心",
    category: "文化",
    location: { lng: 121.464321, lat: 31.164519 }
  },
  {
    id: "BV10039806",
    name: "云锦路地铁站",
    category: "地铁",
    location: { lng: 121.458519, lat: 31.166488 }
  },
  {
    id: "B0JKSO57G0",
    name: "金雀华人美术馆",
    category: "文化",
    location: { lng: 121.463043, lat: 31.1598 }
  }
];

export function createInitialWaitingRouteDemo(
  bandId: WaitingRouteBandId = WAITING_ROUTE_DEFAULT_BAND_ID
): WaitingRouteDemoState {
  return {
    bandId: getWaitingRouteBand(bandId).id,
    revision: 0
  };
}

export function getWaitingRouteBand(bandId: WaitingRouteBandId) {
  return WAITING_ROUTE_BANDS.find((band) => band.id === bandId) ?? WAITING_ROUTE_BANDS[0];
}

export function getWaitingRouteBandIdForMinutes(value: number): WaitingRouteBandId {
  const requestedMinutes = Number.isFinite(value) ? Math.round(value) : WAITING_ROUTE_MIN_MINUTES;
  return requestedMinutes < 13 ? "10-13" : "13-16";
}

export function matchWaitingRouteIntent(
  transcript: string,
  active = false
): WaitingRouteIntent | null {
  const normalized = normalizeWaitingTranscript(transcript);
  const hasScenePhrase = [
    "绕路",
    "绕一会",
    "等人",
    "早到了",
    "分钟后到",
    "晚点到",
    "再到龙耀路"
  ].some((phrase) => normalized.includes(phrase));

  if (!active && !hasScenePhrase) {
    return null;
  }

  const explicitBandId = extractWaitingRouteBandId(normalized);
  const requestedMinutes = explicitBandId
    ? (explicitBandId === "10-13" ? 12 : 15)
    : extractMinutes(normalized);

  if (requestedMinutes === null) {
    return null;
  }

  const bandId = explicitBandId ?? getWaitingRouteBandIdForMinutes(requestedMinutes);
  return {
    requestedMinutes,
    bandId,
    wasClamped:
      requestedMinutes < WAITING_ROUTE_MIN_MINUTES ||
      requestedMinutes > WAITING_ROUTE_MAX_MINUTES
  };
}

function extractWaitingRouteBandId(text: string): WaitingRouteBandId | null {
  const rangeSeparator = "(?:到|至|[-~～—])";

  if (new RegExp(`10${rangeSeparator}13(?:分钟|分|min)`, "i").test(text)) {
    return "10-13";
  }

  if (new RegExp(`13${rangeSeparator}16(?:分钟|分|min)`, "i").test(text)) {
    return "13-16";
  }

  return null;
}

function normalizeWaitingTranscript(transcript: string) {
  return transcript
    .trim()
    .toLowerCase()
    .replace(/[０-９]/g, (character) => String(character.charCodeAt(0) - 0xfee0))
    .replace(/\s+/g, "")
    .replace(/[，。！？,.!?、：:；;]/g, "");
}

function extractMinutes(text: string) {
  const digitMatch = text.match(/(\d{1,3})(?:分钟|分|min)/i);

  if (digitMatch) {
    return Number(digitMatch[1]);
  }

  const chineseMatch = text.match(/(二十|十[一二三四五六七八九]?|[五六七八九])(?:分钟|分)/);

  if (!chineseMatch) {
    return null;
  }

  return parseChineseMinutes(chineseMatch[1]);
}

function parseChineseMinutes(value: string) {
  const digitMap: Record<string, number> = {
    一: 1,
    二: 2,
    三: 3,
    四: 4,
    五: 5,
    六: 6,
    七: 7,
    八: 8,
    九: 9
  };

  if (value === "二十") {
    return 20;
  }

  if (value.startsWith("十")) {
    return 10 + (digitMap[value.slice(1)] ?? 0);
  }

  return digitMap[value] ?? WAITING_ROUTE_MIN_MINUTES;
}
